/**
 * The media family's whole visual vocabulary.
 *
 * Named `*_CLASSES` deliberately, exactly as `selection.tokens.ts`,
 * `structured.tokens.ts` and `scalar.tokens.ts` are: the class law (U19)
 * inspects `cva()` literals and `*_CLASSES` initialisers and nothing else, so a
 * differently-named constant puts its string outside the U7/W3 rules meant to
 * govern it.
 *
 * Two rules, both guarded by `media.tokens.spec.ts`:
 *
 *  - **No physical side.** No `left-`/`right-`/`pl-`/`pr-`/`ml-`/`mr-`/
 *    `text-left`. Every class here is logical (`ps-`, `text-start`, `border-e`)
 *    or axis-neutral, so Arabic is not a second layout to maintain (U7).
 *  - **Every dimension is a NAMED token.** This is the one family that must draw
 *    boxes: an image has an intrinsic size, and without a box a 4000×1200 logo
 *    and a 64×64 icon lay out as two different controls. So `h-media-preview`
 *    and `h-media-tile` are allowed and `h-40` is not — the difference between a
 *    design decision recorded in the preset and a number typed into a component
 *    (U6). The other three families own no box and take their height from the
 *    field surface, which is why their guard can forbid dimensions outright.
 */

/* ── shared ─────────────────────────────────────────────────────────────── */

/** The control's own column: the picture, then the facts, then the actions. */
export const MEDIA_SHELL_CLASSES = 'flex w-full flex-col gap-sm';

/** The read-only notice — visible, not merely announced. */
export const MEDIA_READ_ONLY_CLASSES =
  'flex items-center gap-xs text-caption text-muted-foreground';

/** A row of buttons belonging to the control. Wraps rather than overflowing. */
export const MEDIA_ACTIONS_CLASSES = 'flex flex-wrap items-center gap-sm';

/* ── the preview box ────────────────────────────────────────────────────── */

/**
 * The box every state of the picker is drawn inside.
 *
 * One box for empty, uploading, ready and unavailable, so the control does not
 * change size as it moves between them — a picker that grows by 100px when an
 * upload starts pushes the rest of the form down under the operator's cursor.
 */
export const MEDIA_PREVIEW_CLASSES =
  'relative flex h-media-preview items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40';

/** The same box while it is a drop target and a button. */
export const MEDIA_DROPZONE_CLASSES =
  'flex w-full flex-col items-center justify-center gap-sm border-2 border-dashed border-border p-lg text-caption text-muted-foreground transition-colors duration-fast ease-standard hover:border-primary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60';

/** While a file is over it. */
export const MEDIA_DROPZONE_ACTIVE_CLASSES = 'border-primary bg-primary/5';

/** The invalid treatment — the ring the field surface would have drawn. */
export const MEDIA_INVALID_CLASSES = 'border-destructive';

/**
 * The image itself.
 *
 * `object-contain`, never `object-cover`: a logo cropped to fill a box is a logo
 * the operator cannot judge, and judging it is the entire reason the preview
 * exists. A member's photo grid is the opposite case and is not this control.
 */
export const MEDIA_IMAGE_CLASSES = 'h-full w-full object-contain';

/** The checkerboard behind a transparent PNG, so "white logo" is visible as such. */
export const MEDIA_ALPHA_BACKDROP_CLASSES = 'bg-alpha-grid';

/* ── the facts under the picture ────────────────────────────────────────── */

/** Dimensions, size and type — what the operator needs to judge the asset. */
export const MEDIA_FACTS_CLASSES =
  'flex flex-wrap items-baseline gap-x-sm gap-y-2xs text-caption text-muted-foreground';

/** One fact, kept in Latin digits and LTR whichever way the page reads. */
export const MEDIA_FACT_CLASSES = 'whitespace-nowrap';

/* ── the states that are not a picture ──────────────────────────────────── */

/** Empty, unavailable and uploading all centre a small column of their own. */
export const MEDIA_PLACEHOLDER_CLASSES =
  'flex flex-col items-center justify-center gap-xs p-md text-center text-caption text-muted-foreground';

/** The "this id points at nothing" state — a problem, not a blank. */
export const MEDIA_UNAVAILABLE_CLASSES =
  'flex flex-col items-center justify-center gap-xs p-md text-center text-caption text-warning';

/** The upload progress column: the bar and the percentage under it. */
export const MEDIA_PROGRESS_CLASSES = 'flex w-full flex-col items-center gap-xs px-lg';

export const MEDIA_PROGRESS_BAR_CLASSES = 'w-full';

/** The refusal line under the control. */
export const MEDIA_ERROR_CLASSES = 'text-caption text-destructive';

/* ── verification evidence (P15a) ───────────────────────────────────────── */

/**
 * The box a reviewer reads an ID document in.
 *
 * `h-media-evidence` (18rem) rather than the picker's `h-media-preview` (10rem),
 * and the difference is the whole point: the preview exists to let an operator
 * judge a *logo*, while this one has to let a human read a name and an expiry
 * date off a photographed passport. Scaled into 10rem it is unreadable, and a
 * reviewer would open the full-size dialog on every single case.
 *
 * One box for every state — loading, readable, gone, refused, nothing-sent — so
 * the panel does not resize as it moves between them under the reviewer's cursor.
 */
export const MEDIA_EVIDENCE_CLASSES =
  'relative flex h-media-evidence w-full items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40';

/**
 * The same box at **tile** height — one row of a gallery rather than one document.
 *
 * `h-media-tile` (7rem), not `h-media-evidence` (18rem). The evidence box is
 * sized so a human can read an expiry date off a photographed passport; a venue
 * photograph is being *recognised and ordered*, and eight 18rem rows would be a
 * page nobody can see the shape of. Same control, same four unavailable states,
 * different job — which is why this is a second token rather than a caller's
 * `className` fighting the first (P25a2).
 */
export const MEDIA_EVIDENCE_TILE_CLASSES =
  'relative flex h-media-tile w-full items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40';

/**
 * The evidence image itself.
 *
 * `object-contain` for the same reason the picker's is, only stronger: a cropped
 * ID document is one whose corners — where the expiry and the issuing authority
 * sit — are the part that was cut off.
 */
export const MEDIA_EVIDENCE_IMAGE_CLASSES = 'h-full w-full object-contain';

/**
 * The zoom affordance, pinned to the inline end so it never covers the face or
 * the document number, which sit toward the start in both directions.
 */
export const MEDIA_EVIDENCE_ZOOM_CLASSES = 'absolute end-2xs top-2xs';

/**
 * A caller's marker on the evidence — an advisory badge (P27b2). The opposite
 * corner from the zoom control, still on the inline end: the face and the
 * document number sit toward the start in both directions.
 */
export const MEDIA_EVIDENCE_OVERLAY_CLASSES = 'absolute bottom-2xs end-2xs';

/** The full-size view: as large as the dialog allows, still never cropped. */
export const MEDIA_EVIDENCE_FULL_CLASSES = 'h-auto w-full object-contain';

/**
 * A document frame in the evidence box (F1). It fills the box the image would
 * have; the box keeps its one size across both media, as across every state.
 */
export const MEDIA_EVIDENCE_DOCUMENT_CLASSES = 'h-full w-full border-0 bg-background';

/**
 * The full-size document frame. A frame has no intrinsic size, so `h-auto`
 * would be the browser's 150px default — it takes a named share of the viewport.
 */
export const MEDIA_EVIDENCE_DOCUMENT_FULL_CLASSES = 'h-media-document-full w-full border-0';

/* ── the library dialog ─────────────────────────────────────────────────── */

/** The grid of tiles. Its column count comes from the container, not a breakpoint. */
export const MEDIA_LIBRARY_GRID_CLASSES = 'grid grid-cols-media-library gap-md';

/**
 * One tile. A real `<button>`, because choosing is an action and the grid has to
 * be reachable by keyboard for everyone who does not point at things.
 */
export const MEDIA_TILE_CLASSES =
  'group relative flex h-media-tile items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40 transition-colors duration-fast ease-standard hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/** The tile that is the current value. */
export const MEDIA_TILE_SELECTED_CLASSES = 'border-primary ring-2 ring-ring';

/** The delete affordance on a tile, at the inline end. */
export const MEDIA_TILE_ACTION_CLASSES = 'absolute end-2xs top-2xs';

/** The library's own empty and error states. */
export const MEDIA_LIBRARY_MESSAGE_CLASSES =
  'flex flex-col items-center justify-center gap-sm p-xl text-center text-caption text-muted-foreground';

/** The footer that loads the next page. */
export const MEDIA_LIBRARY_FOOTER_CLASSES = 'flex items-center justify-center pt-md';
